import { useForm, SubmitHandler } from 'react-hook-form';
import { GreenCircleCheckmark } from '../../../svg/GreenCircleCheckmark';
import { Organizations } from '@platform/client';
import { Route, Switch, useLocation } from 'wouter';
import { AuthedPage } from '../../../app';
import { OrgFirstChallengeSelection } from '../../../application/organization/challenges/OrgFirstChallengeSelection';
import { OrgSecondChallengeSelection } from '../../../application/organization/challenges/OrgSecondChallengeSelection';
import { useApplicationLocalStorage } from '../../../application/application-hook';
import { BusinessChallengesRequest } from '@platform/client/schemas';
import { OrgThirdChallengeSelection } from '../../../application/organization/challenges/OrgThirdChallengeSelection';
import { ApplicationPageProps } from '../../../application/Application';
import { ApplicationResultContent } from '../../../application/components/ApplicationResultContent';
import { Button, Spinner } from '@platform/components';

// This form re-uses most of the shared components that make up the org sign up (application) flow
// and thus we are re-using ApplicationPageProps to inform ApplicationBackground and other components
// Other than the awkward naming of props, these components line up fairly nicely with the one
// exception being the progress prop which is passed to each component as 0 as it informs the Steppers

const ChallengesFormPageOne = (props: ApplicationPageProps) => {
  return (
    <OrgFirstChallengeSelection
      onBack={props.onBack}
      onContinue={props.onContinue}
      progress={props.progress}
      isEditProfile={true}
    />
  );
};
const ChallengesFormPageTwo = (props: ApplicationPageProps) => {
  return (
    <OrgSecondChallengeSelection
      onBack={props.onBack}
      onContinue={props.onContinue}
      progress={props.progress}
      isEditProfile={true}
    />
  );
};

const ChallengesFormPageThree = (props: ApplicationPageProps) => {
  const { getApplicationData } = useApplicationLocalStorage<BusinessChallengesRequest>();
  const { handleSubmit } = useForm<BusinessChallengesRequest>();
  const [, navigate] = useLocation();
  const submitBusinessChallengesMutation = Organizations.useCreateBusinessChallenges({
    mutation: {
      onMutate: () => {
        props.onContinue();
      },
      onSuccess: () => {
        navigate('/success');
      },
    },
  });
  const onSubmit: SubmitHandler<BusinessChallengesRequest> = (data) => {
    submitBusinessChallengesMutation.mutate({
      data: {
        ...getApplicationData(),
        third_business_challenge: data.thirdBusinessChallenge,
        third_business_challenge_description: data.thirdBusinessChallengeDescription,
      },
    });
  };
  return (
    <OrgThirdChallengeSelection
      onBack={props.onBack}
      onContinue={handleSubmit(onSubmit)}
      progress={props.progress}
      isEditProfile={true}
    />
  );
};

const ChallengesFormSubmitting = () => {
  return (
    <ApplicationResultContent
      description={
        <div className='flex flex-col items-center gap-4 pt-10'>
          <Spinner />
          <p className='text-lg text-center mt-4'>Submitting your challenges...</p>
        </div>
      }
    ></ApplicationResultContent>
  );
};

const ChallengesFormSuccess = () => {
  return (
    <ApplicationResultContent
      description={
        <div className='flex flex-col items-center gap-4 pt-10'>
          <GreenCircleCheckmark size={80} />
          <p className='text-lg text-center mt-4'>
            Your challenges have been submitted. We will follow up with your hand-picked
            recommendations within 10 days.
          </p>
          <Button
            variant='primary'
            size='md'
            additionalClassnames='w-fit'
            href={`platform/dashboard`}
          >
            Back to dashboard
          </Button>
        </div>
      }
    ></ApplicationResultContent>
  );
};

type ChallengeFormStep = {
  component: React.ComponentType<ApplicationPageProps>;
  url: string;
};

const ChallengeFormSteps: Record<string, ChallengeFormStep> = {
  one: {
    component: ChallengesFormPageOne,
    url: '/',
  },
  two: {
    component: ChallengesFormPageTwo,
    url: '/two',
  },
  three: {
    component: ChallengesFormPageThree,
    url: '/three',
  },
  submitting: {
    component: ChallengesFormSubmitting,
    url: '/submitting',
  },
  success: {
    component: ChallengesFormSuccess,
    url: '/success',
  },
};

export const BusinessChallengesForm = () => {
  const [location, navigate] = useLocation();
  const goToNextStep = () => {
    const steps = Object.keys(ChallengeFormSteps);
    const currentIndex = steps.findIndex((step) => {
      return ChallengeFormSteps[step].url === location;
    });
    const nextStep =
      currentIndex < steps.length - 1 ? steps[currentIndex + 1] : steps[currentIndex];
    const nextUrl = ChallengeFormSteps[nextStep].url;
    navigate(nextUrl);
  };
  const goToPreviousStep = () => {
    const steps = Object.keys(ChallengeFormSteps);
    const currentIndex = steps.findIndex((step) => ChallengeFormSteps[step].url === location);
    const previousStep = currentIndex > 0 ? steps[currentIndex - 1] : steps[currentIndex];
    const previousUrl =
      currentIndex > 0 ? ChallengeFormSteps[previousStep].url : '~/platform/dashboard';
    navigate(previousUrl);
  };
  return (
    <AuthedPage>
      <Switch>
        {Object.entries(ChallengeFormSteps).map(([_key, step]) => {
          const { component: Component, url } = step;
          return (
            <Route key={url} path={url}>
              <Component onBack={goToPreviousStep} onContinue={goToNextStep} progress={0} />
            </Route>
          );
        })}
      </Switch>
    </AuthedPage>
  );
};

export default BusinessChallengesForm;
